'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { useParams, useRouter } from 'next/navigation';
import { Student } from '@/types'; // Assuming Student type is used for display
import { useGuardianChildren, GuardianChildDto } from '@/hooks/useParentData';
import { useToast } from '@/contexts/ToastContext';

interface StudentContextState {
  guardianChildren: GuardianChildDto[] | undefined;
  isLoadingGuardianChildren: boolean;
  errorGuardianChildren: Error | null;
  selectedStudentId: string | null;
  allStudentsForSwitcher: Student[];
  switchStudent: (newStudentId: string) => void;
  setCurrentStudentIdFromUrl: (studentIdFromUrl: string) => void;
}

const StudentContext = createContext<StudentContextState | undefined>(
  undefined,
);

// Natural sort function to handle numeric sorting properly
const naturalSort = (a: string, b: string): number => {
  return a.localeCompare(b, undefined, {
    numeric: true,
    sensitivity: 'base',
    caseFirst: 'lower',
  });
};

export function StudentProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const params = useParams();
  const { addToast } = useToast();

  const currentStudentIdFromUrl =
    typeof params.studentId === 'string' ? params.studentId : null;

  const {
    data: guardianChildren,
    isLoading: isLoadingGuardianChildren,
    isError: isErrorGuardianChildren,
    error: guardianChildrenError,
  } = useGuardianChildren();

  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    () => {
      if (typeof window !== 'undefined') {
        const storedStudentId = localStorage.getItem('selectedStudentId');
        if (storedStudentId) {
          return storedStudentId;
        }
      }
      return currentStudentIdFromUrl;
    },
  );

  // Effect to validate selected student exists in the list
  useEffect(() => {
    if (!isLoadingGuardianChildren && guardianChildren) {
      const studentExists = guardianChildren.some(
        (child) => child.id === selectedStudentId,
      );
      if (selectedStudentId && !studentExists) {
        // If selected student doesn't exist in list, clear selection and notify user
        setSelectedStudentId(null);
        localStorage.removeItem('selectedStudentId');
        addToast(
          'Selected student is no longer available. Please select another student.',
          'info',
        );
      }
    }
  }, [guardianChildren, selectedStudentId, isLoadingGuardianChildren]);

  // Auto-select first student if none is selected and students are available
  useEffect(() => {
    if (
      !selectedStudentId &&
      guardianChildren &&
      guardianChildren.length > 0 &&
      !isLoadingGuardianChildren
    ) {
      const firstStudentId = guardianChildren[0].id;
      setSelectedStudentId(firstStudentId);
      // addToast(`Selected ${guardianChildren[0].firstName} by default`, 'info');
    }
  }, [
    selectedStudentId,
    guardianChildren,
    isLoadingGuardianChildren,
    addToast,
  ]);

  // Handle errors through toasts
  useEffect(() => {
    if (isErrorGuardianChildren && guardianChildrenError) {
      addToast(
        guardianChildrenError.message ||
          "Could not load your children's information. Please try again.",
        'error',
      );
    }
  }, [isErrorGuardianChildren, guardianChildrenError, addToast]);

  useEffect(() => {
    // Persist selectedStudentId to localStorage
    if (typeof window !== 'undefined') {
      if (selectedStudentId) {
        localStorage.setItem('selectedStudentId', selectedStudentId);
      } else {
        // If no student is selected (e.g., after explicit deselection, though not implemented here)
        // you might want to remove it from localStorage.
        // For now, we'll assume a student is always selected if a list exists.
        // Or, if clearing is needed: localStorage.removeItem('selectedStudentId');
      }
    }
  }, [selectedStudentId]);

  const setCurrentStudentIdFromUrl = useCallback((studentIdFromUrl: string) => {
    setSelectedStudentId(studentIdFromUrl);
  }, []);

  const switchStudent = useCallback(
    (newStudentId: string) => {
      if (newStudentId && newStudentId !== selectedStudentId) {
        setSelectedStudentId(newStudentId);
        // router.push(`/children/${newStudentId}`); // Removed navigation
      }
    },
    [selectedStudentId],
  ); // Removed router from dependencies

  const allStudentsForSwitcher: Student[] = React.useMemo(() => {
    if (!guardianChildren) return [];

    const students = guardianChildren.map((child) => ({
      id: child.id,
      name:
        `${child.firstName || ''} ${child.middleName || ''} ${child.lastName || ''}`
          .replace(/\s+/g, ' ')
          .trim() || 'N/A',
      // Add other fields if necessary for the Student type, or adjust the Student type
      rollNumber: child.rollNumber,
      className: child.className,
      sectionName: child.sectionName,
      profileImage: child.profileImage,
      studentUserId: child.studentUserId,
      streamName: child.streamName,
      academicSessionStartDate: child.academicSessionStartDate,
      academicSessionEndDate: child.academicSessionEndDate,
      instituteId: child.instituteId,
      programmeName: child.programmeName,
    }));

    // Sort students by name using natural sorting for proper numeric ordering
    return students?.sort((a, b) => naturalSort(a.name, b.name));
  }, [guardianChildren]);

  return (
    <StudentContext.Provider
      value={{
        guardianChildren,
        isLoadingGuardianChildren,
        errorGuardianChildren: guardianChildrenError,
        selectedStudentId,
        allStudentsForSwitcher,
        switchStudent,
        setCurrentStudentIdFromUrl,
      }}
    >
      {children}
    </StudentContext.Provider>
  );
}

export const useStudentContext = () => {
  const context = useContext(StudentContext);
  if (context === undefined) {
    throw new Error('useStudentContext must be used within a StudentProvider');
  }
  return context;
};
